import { cloneDeep } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import type { Attachment, AttachmentInput } from 'sr-types/lib/production/v1/graphql';
import type { Reference } from 'sr-types/lib/search/v1/graphql';
import Loading from '../components/Loading';
import type { LinkState } from '../components/types';
import { V } from '../Layout';
import { onDeleteFromPreview, onTagChange } from './attachmentsApi';
import AttachmentsEditorFormPanel from './AttachmentsEditorFormPanel';
import AttachmentsLink from './AttachmentsLink';
import AttachmentsPreview from './AttachmentsPreview';
import {
    applySavedAttachmentIds,
    createAttachmentInput,
    EMPTY_LINK,
    hasAttachmentId,
    isNewAttachment
} from './attachmentUtils';
import type { AttachmentLinkDialogTriggerProps } from './types';
import useAttachmentLinkUploader from './useAttachmentLinkUploader';

type AttachmentsEditorProps = AttachmentLinkDialogTriggerProps & {
    entityReference?: Reference;
    maxFileSize?: number;
    acceptedFiles?: Array<string>;
    filesLimit?: number;
    insideSidePanel?: boolean;
    onSave: (entityId: string, input: AttachmentInput[]) => Promise<any>;
    disableDropzone?: boolean;
    filesToEdit?: AttachmentInput[];
    onCancelEdit?: (file: AttachmentInput) => void;
    hideDropzone?: boolean;
    setIsInUploadMode?: Dispatch<SetStateAction<boolean>>;
    defaultMandatoryTags?: Array<string>;
    setLocalAttachments?: Dispatch<SetStateAction<AttachmentInput[]>>;
    localAttachments?: AttachmentInput[];
    setShowAttachmentsViewState?: Dispatch<SetStateAction<boolean>>;
    enableLinkAttachment?: boolean;
    setFilesToEdit?: Dispatch<SetStateAction<AttachmentInput[]>>;
    leftAlignContent?: boolean;
    disableButtons?: boolean;
    autoOpenFilePickerTrigger?: number;
    /** Called after an auto-open file picker request is consumed. */
    onAutoOpenFilePickerHandled?: () => void;
    /** Called after saveAttachments returns ids for newly saved files or links. */
    onSaveAttachments?: (attachments: Attachment[]) => void;
};

export default function AttachmentsEditor({
    entityReference,
    maxFileSize,
    acceptedFiles,
    filesLimit,
    insideSidePanel,
    onSave,
    disableDropzone,
    filesToEdit = [],
    onCancelEdit = () => {},
    hideDropzone = false,
    setIsInUploadMode = () => {},
    defaultMandatoryTags = [],
    localAttachments = [],
    setLocalAttachments,
    setShowAttachmentsViewState,
    enableLinkAttachment = true,
    setFilesToEdit,
    leftAlignContent = false,
    disableButtons = false,
    autoOpenFilePickerTrigger,
    onAutoOpenFilePickerHandled,
    autoOpenLinkDialogTrigger,
    onAutoOpenLinkDialogHandled,
    onSaveAttachments
}: AttachmentsEditorProps) {
    const [saveFiles, setSaveFiles] = useState<Array<AttachmentInput>>([]);
    const [customDeleteFromPreview, setCustomDeleteFromPreview] = useState(false);
    const dropzoneRef = useRef(null);
    const [dropzoneKey, setDropzoneKey] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [hideDropzoneState, setHideDropzoneState] = useState(hideDropzone);
    const [linkState, setLinkState] = useState<LinkState>({ ...cloneDeep(EMPTY_LINK) });
    const [isSavingLinkAttachment, setIsSavingLinkAttachment] = useState(false);
    const onTagChangeHandler = useCallback(
        (name: string, value: string[], index: number) => {
            onTagChange(setSaveFiles)(name, value, index);
        },
        [setSaveFiles]
    );
    const onDeleteFromPreviewHandler = onDeleteFromPreview(filesToEdit, onCancelEdit, dropzoneRef, setSaveFiles);
    const [showAttachmentsPreview, setShowAttachmentsPreview] = useState(false);
    const lastAutoOpenLinkDialogTriggerRef = useRef<number | undefined>(undefined);
    const [filePickerTriggerToForward, setFilePickerTriggerToForward] = useState<number | undefined>(undefined);

    useEffect(() => {
        setFilePickerTriggerToForward(autoOpenFilePickerTrigger);
    }, [autoOpenFilePickerTrigger]);

    const handleAutoOpenFilePickerHandled = useCallback(() => {
        setFilePickerTriggerToForward(undefined);
        onAutoOpenFilePickerHandled?.();
    }, [onAutoOpenFilePickerHandled]);

    useEffect(() => {
        const isSaveFilePresent = saveFiles && saveFiles.length > 0;
        setHideDropzoneState(hideDropzone || isSaveFilePresent);
        setShowAttachmentsViewState?.(!(isSaveFilePresent || showLinkInput));
        setShowAttachmentsPreview(isSaveFilePresent);
    }, [hideDropzone, saveFiles, setShowAttachmentsViewState, showLinkInput, filesToEdit]);

    const { uploadLink } = useAttachmentLinkUploader({
        onSave,
        localAttachments,
        setLocalAttachments,
        setIsLinkDialogOpen: setShowLinkInput,
        entityReferenceId: entityReference?.id,
        setIsSavingLinkAttachment,
        onSaveAttachments
    });

    const handleOpenLinkDialog = useCallback(() => {
        setShowLinkInput(true);
        setLinkState({ ...cloneDeep(EMPTY_LINK) });
        setHideDropzoneState(true);
    }, []);

    const handleCloseLinkDialog = () => {
        setShowLinkInput(false);
        setShowAttachmentsViewState?.(true);
        setHideDropzoneState(hideDropzone);
    };

    useEffect(() => {
        if (autoOpenLinkDialogTrigger === undefined) {
            lastAutoOpenLinkDialogTriggerRef.current = undefined;
            return;
        }

        if (lastAutoOpenLinkDialogTriggerRef.current === autoOpenLinkDialogTrigger) {
            return;
        }

        // Consume blocked requests so they do not open unexpectedly after the disabled state changes.
        lastAutoOpenLinkDialogTriggerRef.current = autoOpenLinkDialogTrigger;

        if (!enableLinkAttachment || disableButtons) {
            onAutoOpenLinkDialogHandled?.();
            return;
        }

        handleOpenLinkDialog();
        onAutoOpenLinkDialogHandled?.();
    }, [
        autoOpenLinkDialogTrigger,
        disableButtons,
        enableLinkAttachment,
        handleOpenLinkDialog,
        onAutoOpenLinkDialogHandled
    ]);

    const onChange = (saveFile) => {
        setSaveFiles((oldFiles) => [...oldFiles, { ...saveFile, tags: [], tagSet: defaultMandatoryTags }]);
        setIsInUploadMode(true);
    };

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const isSaveDisabled = !saveFiles.length;

    useEffect(() => {
        if (filesToEdit.length > 0) {
            setSaveFiles((prevFiles) => {
                setIsInUploadMode(true);
                const existingFilesMap = new Map(prevFiles.map((file) => [file.entityReference?.id, file]));
                const newFiles = filesToEdit.filter((file) => !existingFilesMap.has(file.entityReference?.id));
                if (newFiles.length > 0) {
                    return [...prevFiles, ...newFiles];
                }
                return prevFiles;
            });
        }
    }, [filesToEdit, setIsInUploadMode]);

    const saveAttachment = async () => {
        const payload = saveFiles.map(({ tagSet, ...rest }) => ({
            ...rest,
            tags: tagSet
        }));
        const isCreating = payload.some(isNewAttachment);
        const filesRemovedFromEdit = isCreating
            ? []
            : filesToEdit.filter((file) =>
                  payload.some((item) => item.entityReference?.id === file.entityReference?.id)
              );

        setIsSaving(true);
        setIsInUploadMode(true);
        setSaveFiles([]);
        setDropzoneKey((previous) => previous + 1);
        if (filesRemovedFromEdit.length > 0) {
            setFilesToEdit?.((current) =>
                current.filter((file) => !payload.some((item) => item.entityReference?.id === file.entityReference?.id))
            );
        }

        let savedAttachments: Attachment[] = [];
        let didSaveFail = true;
        try {
            const response = await onSave(entityReference?.id, payload);
            if (response) {
                savedAttachments = applySavedAttachmentIds(payload, response).filter(hasAttachmentId) as Attachment[];
                didSaveFail = false;
            }
        } finally {
            if (didSaveFail) {
                const restoredPayload = payload.map((file) => ({ ...file, tagSet: file.tags ?? [] }));
                setSaveFiles((current) => {
                    const currentIds = new Set(current.map((file) => file.entityReference?.id));
                    const missingFromCurrent = restoredPayload.filter(
                        (file) => !currentIds.has(file.entityReference?.id)
                    );
                    return missingFromCurrent.length > 0 ? [...missingFromCurrent, ...current] : current;
                });
                if (filesRemovedFromEdit.length > 0) {
                    setFilesToEdit?.((current) => {
                        const currentIds = new Set(current.map((file) => file.entityReference?.id));
                        const restored = filesRemovedFromEdit.filter(
                            (file) => !currentIds.has(file.entityReference?.id)
                        );
                        return restored.length > 0 ? [...current, ...restored] : current;
                    });
                }
            }
            setIsSaving(false);
            setIsInUploadMode(false);
        }

        if (savedAttachments.length > 0) {
            onSaveAttachments?.(savedAttachments);
        }
    };

    const handleSaveLink = () => {
        uploadLink(linkState.linkURL, linkState.linkName, linkState.tags);
        setShowLinkInput(false);
        setHideDropzoneState(hideDropzone);
    };

    const hideButtonsAndInfo = saveFiles && saveFiles.length > 0;

    const onCancelHandle = () => {
        setIsInUploadMode(false);
        setFilesToEdit([]);
        setSaveFiles([]);
        setShowAttachmentsViewState?.(true);
    };
    return (
        <>
            {isUploading ? (
                <Loading />
            ) : (
                <>
                    {showLinkInput ? (
                        <AttachmentsLink
                            onSave={handleSaveLink}
                            setLinkState={setLinkState}
                            linkState={linkState}
                            isSavingLinkAttachment={isSavingLinkAttachment}
                            open={showLinkInput}
                            onClose={handleCloseLinkDialog}
                        />
                    ) : (
                        <>
                            <V>
                                {showAttachmentsPreview && (
                                    <AttachmentsPreview
                                        saveFiles={saveFiles}
                                        onDelete={onDeleteFromPreviewHandler}
                                        onTagChange={onTagChangeHandler}
                                        defaultMandatoryTags={defaultMandatoryTags}
                                        saveAttachment={saveAttachment}
                                        isSaveDisabled={isSaveDisabled}
                                        isSaving={isSaving}
                                        onCancelEdit={onCancelHandle}
                                    />
                                )}
                            </V>
                            <AttachmentsEditorFormPanel
                                insideSidePanel={insideSidePanel}
                                hideDropzoneState={hideDropzoneState}
                                dropzoneRef={dropzoneRef}
                                dropzoneKey={dropzoneKey}
                                entityReference={entityReference}
                                maxFileSize={maxFileSize}
                                acceptedFiles={acceptedFiles}
                                filesLimit={filesLimit}
                                onChange={onChange}
                                customDeleteFromPreview={customDeleteFromPreview}
                                saveFiles={saveFiles}
                                disableDropzone={disableDropzone}
                                hideButtonsAndInfo={hideButtonsAndInfo}
                                setIsUploading={setIsUploading}
                                setSaveFiles={setSaveFiles}
                                createAttachmentInput={createAttachmentInput}
                                defaultMandatoryTags={defaultMandatoryTags}
                                handleOpenLinkDialog={handleOpenLinkDialog}
                                enableLinkAttachment={enableLinkAttachment}
                                leftAlignContent={leftAlignContent}
                                disableButtons={disableButtons}
                                autoOpenFilePickerTrigger={filePickerTriggerToForward}
                                onAutoOpenFilePickerHandled={handleAutoOpenFilePickerHandled}
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
